import { CompleteKundali, DashaPeriod, PlanetName, Language } from '../types/kundali';
import { ChartArchetypeProfile, identifyChartArchetype } from './humanizedToneEngine';
import { calculatePratyantardashas } from './dashaCalculator';

export interface DashaEvolutionPhase {
  level: 'mahadasha' | 'antardasha' | 'pratyantardasha';
  mahadashaLord: PlanetName;
  antardashaLord?: PlanetName;
  pratyantardashaLord?: PlanetName;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isPast: boolean;
  isFuture: boolean;
  phaseTitleEn: string;
  phaseTitleHi: string;
  phaseTitleHinglish: string;
  dominantPsychology: {
    en: string;
    hi: string;
    hinglish: string;
  };
  leadershipAndAgency: {
    en: string;
    hi: string;
    hinglish: string;
  };
  riskAppetiteAndWealth: {
    en: string;
    hi: string;
    hinglish: string;
  };
  dailyOperationalFocus: {
    en: string;
    hi: string;
    hinglish: string;
  };
  keyTransformationNarrative: {
    en: string;
    hi: string;
    hinglish: string;
  };
  // Nested sub-phases
  antardashas?: DashaEvolutionPhase[];
  pratyantardashas?: DashaEvolutionPhase[];
}

export interface DashaTransitionBridge {
  fromPlanet: PlanetName;
  toPlanet: PlanetName;
  transitionDate: string;
  transitionTitleEn: string;
  transitionTitleHi: string;
  transitionTitleHinglish: string;
  orientationShiftQuote: {
    en: string;
    hi: string;
    hinglish: string;
  };
  beforeState: {
    en: string;
    hi: string;
    hinglish: string;
  };
  afterState: {
    en: string;
    hi: string;
    hinglish: string;
  };
  actionAdvice: {
    en: string;
    hi: string;
    hinglish: string;
  };
}

export interface DashaEvolutionReport {
  archetype: ChartArchetypeProfile;
  currentMahadasha: DashaPeriod | null;
  currentAntardasha: DashaPeriod | null;
  nextMahadasha: DashaPeriod | null;
  activePhaseSummary: DashaEvolutionPhase;
  upcomingTransitions: DashaTransitionBridge[];
  allChronologicalPhases: DashaEvolutionPhase[];
}

/**
 * Get specialized transition logic between consecutive planetary Mahadashas
 */
export function getMahadashaTransitionShift(
  fromPlanet: PlanetName,
  toPlanet: PlanetName,
  archetype: ChartArchetypeProfile,
  transitionDate: string
): DashaTransitionBridge {
  // 1. Saturn -> Mercury
  if (fromPlanet === 'Saturn' && toPlanet === 'Mercury') {
    return {
      fromPlanet,
      toPlanet,
      transitionDate,
      transitionTitleEn: 'From Operational Endurance to Strategic & Commercial Scaling',
      transitionTitleHi: 'कठिन परिश्रम और धैर्य से रणनीतिक एवं व्यावसायिक विस्तार की ओर',
      transitionTitleHinglish: 'Saturn to Mercury Shift: Deep Execution se Strategic Scaling tak',
      orientationShiftQuote: {
        en: 'In your previous Saturn phase, your orientation was grounded in operational grind, endurance, and system building; after this dasha, your style transforms decisively into strategic scaling, commercial growth, sharp networking, and broader advisory roles.',
        hi: 'शनि की अवधि में आपका ध्यान कठिन परिश्रम, सहनशक्ति और व्यवस्था निर्माण पर था; बुध दशा के आगमन से आपका कार्य-शैली रणनीतिक विस्तार, व्यावसायिक लाभ, वाक-चातुर्य और सलाहकार नेतृत्व में रूपांतरित होगी।',
        hinglish: 'Is phase mein aapka orientation deep execution, thehraav aur operational grind tha, par is dasha ke baad aapka style badal kar strategic scaling, sharp networking aur commercial executive mindset mein transform hoga.'
      },
      beforeState: {
        en: 'Carrying heavy burdens, grinding through bureaucratic hurdles, slow compounding, heavy discipline with limited immediate rewards.',
        hi: 'कठोर उत्तरदायित्व, धीमी गति, निरंतर अनुशासन और तात्कालिक लाभ के बिना धैर्यपूर्वक कार्य।',
        hinglish: 'Bhari zimmedari, back-office kaam, slow par solid progress aur bina short-cut ke daily grind.'
      },
      afterState: {
        en: 'Light on feet, swift commercial deals, monetizing knowledge, vibrant communication, building strategic alliances and diversified revenue.',
        hi: 'त्वरित निर्णय, व्यापारिक अनुबंध, संपर्कों का विस्तार, बौद्धिक पूंजी का मुद्रीकरण और बहुआयामी आय।',
        hinglish: 'Smart jugaad, commercial deals, brand articulation, networking aur multiple income streams.'
      },
      actionAdvice: {
        en: 'Drop the heavy survival posture and invest in public communication, product marketing, and modern technology frameworks.',
        hi: 'अनावश्यक बोझ से मुक्त होकर सार्वजनिक संवाद, विपणन और तकनीकी दक्षता में निवेश करें।',
        hinglish: 'Thoda heavy stress chhodkar presentation, smart marketing aur communication pe invest karo.'
      }
    };
  }

  // 2. Rahu -> Jupiter
  if (fromPlanet === 'Rahu' && toPlanet === 'Jupiter') {
    return {
      fromPlanet,
      toPlanet,
      transitionDate,
      transitionTitleEn: 'From Restless Ambition to Grounded Wisdom & Institutional Authority',
      transitionTitleHi: 'अस्थिर महत्वाकांक्षा से स्थायी विवेक, सम्मान और संस्थागत नेतृत्व की ओर',
      transitionTitleHinglish: 'Rahu to Jupiter Shift: Aggressive Wandering se Grounded Wisdom tak',
      orientationShiftQuote: {
        en: 'In your Rahu phase, your orientation was driven by aggressive trial-and-error, unconventional ambition, and intense restlessness; after this dasha, your style transforms into stable wisdom, mentorship, structural authority, and respected ethical leadership.',
        hi: 'राहु काल में आपकी प्रेरणा तीव्र महत्वाकांक्षा, अनूठे प्रयोगों और निरंतर असंतोष से संचालित थी; गुरु दशा में आपका स्वरूप गरिमामय विवेक, समाज में प्रतिष्ठा और मार्गदर्शक नेतृत्व में विकसित होगा।',
        hinglish: 'Is phase mein aapka orientation aggressive trial-and-error aur relentless ambition tha, par is dasha ke baad aapka style badal kar stable wisdom, mentorship aur respected leadership mindset mein transform hoga.'
      },
      beforeState: {
        en: 'Chasing rapid scale, navigating high-risk experiments, unpredictable spikes in status, intense internal anxiety and identity searches.',
        hi: 'जोखिम भरे प्रयोग, तीव्र उतार-चढ़ाव, मानसिक व्यग्रता और पहचान की निरंतर खोज।',
        hinglish: 'High-risk experiment, restless energy, status badhane ki aag aur sleepless nights.'
      },
      afterState: {
        en: 'Anchored peace, recognized subject matter expertise, moral clarity, nurturing others, and commanding natural respect without posturing.',
        hi: 'मानसिक शांति, विषय की विशेषज्ञता, नैतिक स्पष्टता और स्वाभाविक आदर-सम्मान।',
        hinglish: 'Apnapan, public respect, ethical clarity, mentorship aur stable long-term assets.'
      },
      actionAdvice: {
        en: 'Transition from chasing vanity metrics to building enduring institutional value and mentoring the next generation.',
        hi: 'अस्थायी चमक-दमक के स्थान पर स्थायी प्रतिष्ठा और मार्गदर्शन का संकल्प लें।',
        hinglish: 'Show-off aur shortcut chhodkar substantive value aur authentic guidance par focus karein.'
      }
    };
  }

  // 3. Mercury -> Ketu
  if (fromPlanet === 'Mercury' && toPlanet === 'Ketu') {
    return {
      fromPlanet,
      toPlanet,
      transitionDate,
      transitionTitleEn: 'From Analytical Commerce to Inward Calibration & Spiritual Mastery',
      transitionTitleHi: 'व्यापारिक विश्लेषण से आत्म-अवलोकन, वैराग्य एवं आध्यात्मिक गहराई की ओर',
      transitionTitleHinglish: 'Mercury to Ketu Shift: Tactical Commerce se Inward Depth tak',
      orientationShiftQuote: {
        en: 'In your Mercury phase, your orientation was centered on tactical data, communication, and commercial negotiations; after this transition, your style transforms inward into deep analytical detachment, research, intuitive mastery, and shedding superficial baggage.',
        hi: 'बुध काल में आपका ध्यान बौद्धिक संवाद और व्यावसायिक गणनाओं पर केंद्रित था; केतु के आगमन से आपकी चेतना गहन आत्म-शोध, अनुसंधान और आध्यात्मिक स्पष्टता में रूपांतरित होगी।',
        hinglish: 'Is phase mein aapka orientation tactical data aur networking tha, par is dasha ke baad aapka style badal kar internal clarity, deep research aur non-attachment mindset mein transform hoga.'
      },
      beforeState: {
        en: 'Constant mental multitasking, active social circles, daily spreadsheets, and commercial calculations.',
        hi: 'निरंतर मानसिक सक्रियता, विस्तृत सामाजिक संपर्क और सांसारिक हिसाब-किताब।',
        hinglish: 'Continuous chatting, networking, deals aur commercial hisaab-kitaab.'
      },
      afterState: {
        en: 'Pruning unwanted relationships, laser-focused deep work, philosophical breakthrough, and shedding superficial ego.',
        hi: 'अनावश्यक संबंधों का त्याग, एकाग्र गहन शोध और आत्मिक उत्थान।',
        hinglish: 'Clutter clear karna, deep focus, niche mastery aur spiritual peace.'
      },
      actionAdvice: {
        en: 'Do not fight the urge to step back; use this window for deep R&D, spiritual refinement, and decluttering your obligations.',
        hi: 'इस समय का उपयोग गहन शोध, कौशल निखार और मानसिक शांति के लिए करें।',
        hinglish: 'Sabse validate hone ki zaroorat nahi hai; apne niche core skill aur mental detox pe kaam karo.'
      }
    };
  }

  // 4. Ketu -> Venus
  if (fromPlanet === 'Ketu' && toPlanet === 'Venus') {
    return {
      fromPlanet,
      toPlanet,
      transitionDate,
      transitionTitleEn: 'From Introspective Hermit to Magnetic Public Brand & Refined Abundance',
      transitionTitleHi: 'अंतर्मुखी साधना से कलात्मक सौंदर्य, वैभव और सामाजिक प्रतिष्ठा की ओर',
      transitionTitleHinglish: 'Ketu to Venus Shift: Solitude se Magnetic Brand & Luxury tak',
      orientationShiftQuote: {
        en: 'In your Ketu phase, your orientation was introspective, detached, and solitary; after entering Venus, your style transforms into public charisma, refined aesthetics, lucrative brand building, and luxurious relational harmony.',
        hi: 'केतु की अवधि में आप एकांत और आत्ममंथन में थे; शुक्र के प्रवेश से आपका जीवन चुंबकीय आकर्षण, ऐश्वर्य, कलात्मक सुरुचि और समृद्ध संबंधों में रूपांतरित होगा।',
        hinglish: 'Is phase mein aapka orientation detachment aur solitary work tha, par is dasha ke baad aapka style badal kar public charisma, refined taste aur luxury brand leadership mein transform hoga.'
      },
      beforeState: {
        en: 'Solitary focus, feeling misunderstood by worldly peers, minimal material desires, and intense spiritual filtering.',
        hi: 'एकांतप्रियता, सांसारिक विमुखता और भौतिक आकांक्षाओं की कमी।',
        hinglish: 'Low social media presence, minimal material desire aur deep introspection.'
      },
      afterState: {
        en: 'High social magnetism, luxury lifestyle upgrades, artistic appreciation, lucrative joint ventures, and harmonious partnerships.',
        hi: 'सामाजिक आकर्षण, सुख-सुविधाओं की वृद्धि, सुरुचिपूर्ण जीवन और लाभकारी साझेदारियां।',
        hinglish: 'Public recognition, aesthetic appeal, brand value aur comfortable lifestyle.'
      },
      actionAdvice: {
        en: 'Re-emerge onto the public stage with refined packaging, upgrade your environment, and lean into aesthetic partnerships.',
        hi: 'सार्वजनिक मंच पर पुनः सक्रिय हों और अपने परिवेश व संबंधों को सुरुचिपूर्ण बनाएं।',
        hinglish: 'Apne personal brand aur creative projects ko front-row pe le aao.'
      }
    };
  }

  // 5. Sun / Mars Activation
  if (['Sun', 'Mars'].includes(toPlanet)) {
    return {
      fromPlanet,
      toPlanet,
      transitionDate,
      transitionTitleEn: `Activation of Frontline Executive Command & Sovereign Agency (${toPlanet})`,
      transitionTitleHi: `अग्रिम पंक्ति का नेतृत्व, अधिकार एवं त्वरित निर्णय क्षमता (${toPlanet} प्रभाव)`,
      transitionTitleHinglish: `${toPlanet} Dasha Activation: Front-Foot Command & Direct Ownership`,
      orientationShiftQuote: {
        en: `In your prior ${fromPlanet} phase, your stance was more adaptive or supportive; entering ${toPlanet} ignites immediate frontline command, uncompromising boundary defense, and sovereign decision-making.`,
        hi: `${fromPlanet} की अवधि में आपका दृष्टिकोण सहयोगी था; ${toPlanet} के प्रभाव से आपमें सर्वोच्च अधिकार, निर्भीक नेतृत्व और त्वरित निर्णय लेने का साहस जाग्रत होगा।`,
        hinglish: `Is phase mein aapka orientation thoda wait-and-watch ya accommodative tha, par is ${toPlanet} dasha ke baad aapka style badal kar front-foot ownership aur decisive command mindset mein transform hoga.`
      },
      beforeState: {
        en: 'Consulting consensus, accommodating external delays, soft negotiating posture.',
        hi: 'दूसरों पर निर्भरता, सहमति की प्रतीक्षा और शांत दृष्टिकोण।',
        hinglish: 'Doosron ke approval ka wait karna aur soft approach rakhna.'
      },
      afterState: {
        en: 'Taking the captaincy seat, direct execution, zero tolerance for mediocrity, conquering competitive territory.',
        hi: 'पूर्ण नेतृत्व, निर्भीक निर्णय, प्रतिस्पर्धियों पर विजय और स्वतंत्र कार्यप्रणाली।',
        hinglish: 'Frontline pe aakar lead karna, clear boundaries set karna aur goals achieve karna.'
      },
      actionAdvice: {
        en: 'Step into positions of uncompromising authority, speak with clarity, and lead your team without second-guessing your mandate.',
        hi: 'आत्मविश्वास के साथ नेतृत्व संभालें और अपने अधिकारों का सदुपयोग करें।',
        hinglish: 'Apne authority roles ko hesitate mat karo; clear decisions lo aur team ko lead karo.'
      }
    };
  }

  // 6. Generic/Universal Bridge
  return {
    fromPlanet,
    toPlanet,
    transitionDate,
    transitionTitleEn: `Planetary Evolution: ${fromPlanet} to ${toPlanet}`,
    transitionTitleHi: `ग्रहीय दशा परिवर्तन: ${fromPlanet} से ${toPlanet}`,
    transitionTitleHinglish: `${fromPlanet} to ${toPlanet} Dasha Transition: Orientation Shift`,
    orientationShiftQuote: {
      en: `In your ${fromPlanet} cycle, your orientation emphasized the themes of ${fromPlanet}; entering ${toPlanet} fundamentally realigns your daily focus, leadership drive, and risk appetite toward ${toPlanet}'s core evolutionary purpose.`,
      hi: `${fromPlanet} दशा में आपकी प्राथमिकताएं तत्कालीन परिस्थितियों के अनुसार थीं; ${toPlanet} के आगमन से आपके दृष्टिकोण और कार्यशैली में महत्वपूर्ण रूपांतरण होगा।`,
      hinglish: `Is phase mein aapka orientation ${fromPlanet} ki energy pe aligned tha, par is dasha ke baad aapka style badal kar ${toPlanet} ki dynamic leadership aur transformed mindset mein adapt hoga.`
    },
    beforeState: {
      en: `Operating under the archetypal lens and priorities of ${fromPlanet}.`,
      hi: `${fromPlanet} ग्रह के प्रभाव में निर्धारित प्राथमिकताएं।`,
      hinglish: `${fromPlanet} ke anusaar focus aur execution style.`
    },
    afterState: {
      en: `Stepping into the expanded dimensional opportunities governed by ${toPlanet}.`,
      hi: `${toPlanet} के द्वारा प्रेरित नई संभावनाएं और लक्ष्य।`,
      hinglish: `${toPlanet} ki energy ke sath naye goals aur executive orientation.`
    },
    actionAdvice: {
      en: `Harmonize with the incoming planetary frequency through deliberate lifestyle calibration.`,
      hi: `आने वाली दशा के अनुरूप अपनी प्राथमिकताओं का पुनर्गठन करें।`,
      hinglish: `Naye phase ke hisaab se apni daily routine aur priorities ko align karein.`
    }
  };
}

export const PLANET_YEARS: Record<PlanetName, number> = {
  Ketu: 7,
  Venus: 20,
  Sun: 6,
  Moon: 10,
  Mars: 7,
  Rahu: 18,
  Jupiter: 16,
  Saturn: 19,
  Mercury: 17,
  Ascendant: 0
};

export const DASHA_SEQUENCE: PlanetName[] = [
  'Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury'
];

/**
 * Generate Dasha Phase Evolution Details for a given Mahadasha Lord
 */
export function getDashaPhaseDetails(
  planet: PlanetName,
  startDate: string,
  endDate: string,
  isCurrent: boolean,
  isPast: boolean,
  isFuture: boolean,
  archetype: ChartArchetypeProfile
): DashaEvolutionPhase {
  let phase: DashaEvolutionPhase;
  switch (planet) {
    case 'Saturn':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Saturn',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Saturn Mahadasha (19 Years): The Great Builder & Operational Crucible',
        phaseTitleHi: 'शनि महादशा (19 वर्ष): कर्म की साधना एवं ठोस आधार निर्माण',
        phaseTitleHinglish: 'Saturn Dasha (19 Years): Thehraav, System-First & Heavy Endurance',
        dominantPsychology: {
          en: 'Grounded realism, high stoicism, extreme patience, and respect for natural compounding.',
          hi: 'यथार्थवादी दृष्टिकोण, गहन धैर्य, अनुशासन और क्रमिक विकास में विश्वास।',
          hinglish: 'Zero drama mindset, extreme patience aur daily routine pe bina shikayat tikke rehna.'
        },
        leadershipAndAgency: {
          en: 'Leading through relentless consistency, operational reliability, and shouldering heavy responsibility.',
          hi: 'कठिन जिम्मेदारियों को स्वयं वहन कर एक विश्वसनीय स्तंभ के रूप में नेतृत्व।',
          hinglish: 'Front pe show-off nahi, par backend operations ko akele sambhalne wali solid leadership.'
        },
        riskAppetiteAndWealth: {
          en: 'Ultra-conservative risk profile; preference for hard physical assets, fixed compounding, and zero speculative waste.',
          hi: 'अत्यंत सुरक्षित निवेश, अचल संपत्ति और सट्टेबाजी से पूर्ण दूरी।',
          hinglish: 'Zero speculation; SIP, property aur safe compounding assets par 100% bharosa.'
        },
        dailyOperationalFocus: {
          en: 'Streamlining processes, resolving organizational bottlenecks, and enduring prolonged workloads.',
          hi: 'व्यवस्थाओं को सुदृढ़ करना और दीर्घकालिक लक्ष्यों के लिए अनवरत कार्य।',
          hinglish: 'System theek karna, pending kaam niptana aur continuous work ethics maintain karna.'
        },
        keyTransformationNarrative: {
          en: 'This phase strips away delusions of quick fame. It forces you to build unshakeable character and structural foundations that survive any crisis.',
          hi: 'यह काल तात्कालिक चमक-दमक से परे आपको जीवन की सबसे मजबूत नींव और अनुशासन प्रदान करता है।',
          hinglish: 'Yeh period aapko pakka kar deta hai—koi bhi shortcut fail ho sakta hai, par is phase mein banayi gayi foundation lifetime protect karegi.'
        }
      };
      break;

    case 'Mercury':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Mercury',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Mercury Mahadasha (17 Years): Commercial Mastery & Strategic Intelligence',
        phaseTitleHi: 'बुध महादशा (17 वर्ष): व्यापारिक उन्नति एवं बौद्धिक विस्तार',
        phaseTitleHinglish: 'Mercury Dasha (17 Years): Smart Jugaad, Networking & Commercial Growth',
        dominantPsychology: {
          en: 'Agile cognition, rapid adaptability, commercial acumen, and insatiable curiosity.',
          hi: 'कुशाग्र बुद्धि, व्यापारिक सूझबूझ, वाक-चातुर्य और नई संभावनाओं की खोज।',
          hinglish: 'Sharp analytical dimaag, fast communication aur profitable opportunities capture karna.'
        },
        leadershipAndAgency: {
          en: 'Influence through eloquent communication, data-backed negotiation, and agile consensus building.',
          hi: 'तर्कसंगत संवाद, कुशल मध्यस्थता और बौद्धिक नेतृत्व द्वारा प्रभाव स्थापित करना।',
          hinglish: 'Articulate negotiation, smart networking aur smart deals crack karne wali leadership.'
        },
        riskAppetiteAndWealth: {
          en: 'Calculated, market-tested trading, IP creation, multiple income streams, and diversified capital.',
          hi: 'विविध आय स्रोत, बौद्धिक संपदा और विचारशील व्यापारिक निवेश।',
          hinglish: 'Multiple streams of income, smart consulting aur scalable commercial growth.'
        },
        dailyOperationalFocus: {
          en: 'Pitching, contract finalization, writing, digital systems, and collaborative partnerships.',
          hi: 'संवाद, अनुबंध, तकनीकी प्रबंधन और रणनीतिक साझेदारियों का क्रियान्वयन।',
          hinglish: 'Email/messaging, networking, client meetings aur digital branding.'
        },
        keyTransformationNarrative: {
          en: 'This phase liberates your voice and intellect. You transition from manual labor to intellectual leverage and strategic wealth creation.',
          hi: 'यह काल आपकी बौद्धिक क्षमताओं को खोलता है और ज्ञान को स्थायी समृद्धि में बदलता है।',
          hinglish: 'Yahan aap physical hard-work se smart intellectual leverage par shift ho jaate ho.'
        }
      };
      break;

    case 'Ketu':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Ketu',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Ketu Mahadasha (7 Years): Karmic Introspection & Spiritual Deconstruction',
        phaseTitleHi: 'केतु महादशा (7 वर्ष): आत्म-अनुसंधान एवं आध्यात्मिक शुद्धि',
        phaseTitleHinglish: 'Ketu Dasha (7 Years): Detachment, Deep Research & Inner Calibration',
        dominantPsychology: {
          en: 'Introspective, seeking deeper existential meaning, shedding vanity, and razor-sharp intuition.',
          hi: 'अंतर्मुखी चेतना, दिखावे से विरक्ति, गहन अंतर्ज्ञान और आत्मिक अनुसंधान।',
          hinglish: 'Duniya ke show-off se door, deep self-discovery aur razor-sharp intuition.'
        },
        leadershipAndAgency: {
          en: 'Behind-the-scenes master strategist, quiet adviser, and radical problem-solver who sees what others miss.',
          hi: 'पर्दे के पीछे से गुप्त रणनीति और गहन अनुसंधान आधारित मार्गदर्शन।',
          hinglish: 'Quiet advisory role; bina limelight ke core solution develop karna.'
        },
        riskAppetiteAndWealth: {
          en: 'Selective detachment; money is treated as a practical tool rather than an identity validator.',
          hi: 'भौतिक मोह से मुक्ति; धन को केवल आवश्यकता और साधन के रूप में देखना।',
          hinglish: 'Money se emotional attachment khatam, specialized skill monetization.'
        },
        dailyOperationalFocus: {
          en: 'Solitary deep research, cutting toxic ties, meditation, and specialized technical mastery.',
          hi: 'एकांत साधना, अनावश्यक बोझ की समाप्ति और गूढ़ विद्याओं का अध्ययन।',
          hinglish: 'Decluttering, coding/research/writing aur spiritual sadhana.'
        },
        keyTransformationNarrative: {
          en: 'Ketu cuts the strings of false ego. It liberates you from superficial rat-races and tunes you into timeless truth.',
          hi: 'केतु भ्रम के बंधनों को काटकर आपको वास्तविक सत्य और आत्म-संतुष्टि प्रदान करता है।',
          hinglish: 'Yeh dasha fake logon aur superficial validation ko hata kar aapko ekdum genuine banati hai.'
        }
      };
      break;

    case 'Venus':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Venus',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Venus Mahadasha (20 Years): Magnetic Magnetism, Relational Bliss & Creative Abundance',
        phaseTitleHi: 'शुक्र महादशा (20 वर्ष): ऐश्वर्य, कलात्मक सुरुचि एवं समृद्ध संबंध',
        phaseTitleHinglish: 'Venus Dasha (20 Years): Charisma, Luxury Lifestyle & Harmonious Brand',
        dominantPsychology: {
          en: 'Celebratory, aesthetic appreciation, high social grace, and focus on luxury and comfort.',
          hi: 'आनंदमयी जीवन, सुरुचिपूर्ण वातावरण, सामाजिक सौहार्द और सुख-सुविधाओं का विस्तार।',
          hinglish: 'Aesthetic living, relationships mein harmony aur high quality lifestyle ka anand.'
        },
        leadershipAndAgency: {
          en: 'Charismatic brand leadership, diplomatic alliance-building, and creating visually appealing ecosystems.',
          hi: 'आकर्षक व्यक्तित्व, सौहार्दपूर्ण कूटनीति और सामूहिक सहयोग द्वारा नेतृत्व।',
          hinglish: 'Public appeal, brand charisma aur mutually beneficial partnerships.'
        },
        riskAppetiteAndWealth: {
          en: 'High luxury consumption balanced by lucrative artistic, design, or commercial ventures.',
          hi: 'कला, विलासिता, आभूषण और सुरुचिपूर्ण व्यवसायों से प्रचुर धन प्राप्ति।',
          hinglish: 'Creative & luxury investments; brand value multiply hona.'
        },
        dailyOperationalFocus: {
          en: 'Hospitality, interior refinement, creative projects, high-value styling, and networking dinners.',
          hi: 'सौंदर्य संवर्धन, पारिवारिक सुख, कलात्मक गतिविधियां और सामाजिक आयोजन।',
          hinglish: 'Creative styling, luxury travel, social relationships aur quality experiences.'
        },
        keyTransformationNarrative: {
          en: 'This 20-year golden cycle rewards you with worldly beauty, relational harmony, and artistic fulfillment.',
          hi: 'यह काल जीवन में सौंदर्य, प्रेम, सम्मान और संपन्नता का स्वर्णिम द्वार खोलता है।',
          hinglish: 'Life mein luxury, comfort aur genuine companionship enjoy karne ka best time.'
        }
      };
      break;

    case 'Sun':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Sun',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Sun Mahadasha (6 Years): Sovereign Authority & High-Visibility Power',
        phaseTitleHi: 'सूर्य महादशा (6 वर्ष): राजयोग, आत्म-तेज एवं प्रशासनिक प्रतिष्ठा',
        phaseTitleHinglish: 'Sun Dasha (6 Years): Royal Authority, High Agency & Front-Row Leadership',
        dominantPsychology: {
          en: 'Sovereign confidence, unyielding self-respect, moral dignity, and clear executive clarity.',
          hi: 'आत्मविश्वास, स्वाभिमान, नेतृत्व का संकल्प और सत्यनिष्ठा।',
          hinglish: 'High-octane self-respect, direct clarity aur authority se command karna.'
        },
        leadershipAndAgency: {
          en: 'Direct sovereign command, government/institutional connections, taking unconditional accountability.',
          hi: 'उच्च प्रशासनिक अधिकार, संस्थागत प्रभुत्व और प्रत्यक्ष निर्णय-क्षमता।',
          hinglish: 'Direct leadership, board-level decisions aur institutional influence.'
        },
        riskAppetiteAndWealth: {
          en: 'Bold executive equity, high-status ventures, government contracts, and prestigious holdings.',
          hi: 'प्रतिष्ठित संपत्तियां, सरकारी अनुबंध और सम्मानजनक धन लाभ।',
          hinglish: 'High-status deals, executive authority aur institutional wealth.'
        },
        dailyOperationalFocus: {
          en: 'Setting organizational vision, delegating lower-tier chores, and commanding key boardroom decisions.',
          hi: 'नीति-निर्धारण, महत्वपूर्ण निर्णय और प्रशासनिक व्यवस्था का संचालन।',
          hinglish: 'Big picture strategy, key decisions lena aur leadership mandate execute karna.'
        },
        keyTransformationNarrative: {
          en: 'The Sun places you firmly at center-stage. It forces you to claim your natural seat of power without hesitation.',
          hi: 'सूर्य आपको समाज और कार्यक्षेत्र के केंद्र में लाकर वास्तविक नेतृत्व का अवसर देता है।',
          hinglish: 'Aap front-row seat pe aa jaate ho; log aapke command aur decision ki taraf dekhte hain.'
        }
      };
      break;

    case 'Moon':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Moon',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Moon Mahadasha (10 Years): Emotional Resonance & Public Popularity',
        phaseTitleHi: 'चंद्र महादशा (10 वर्ष): जन-प्रियता, संवेदनशीलता एवं मानसिक शांति',
        phaseTitleHinglish: 'Moon Dasha (10 Years): Apnapan, Public Connect & Intuitive Flow',
        dominantPsychology: {
          en: 'Empathetic, fluid, intuitive, craving emotional safety and close family nurturing.',
          hi: 'संवेदनशील मन, मातृत्व भाव, जन-संपर्क और मानसिक शांति की खोज।',
          hinglish: 'Heart-first approach, intuitive connect aur logon ke sath gehra apnapan.'
        },
        leadershipAndAgency: {
          en: 'Empathetic leadership that rallies public sentiment and wins deep grassroots loyalty.',
          hi: 'जन-भावनाओं को समझने वाला और सबका विश्वास जीतने वाला नेतृत्व।',
          hinglish: 'Logon ke dilon ko jeetne wali leadership; high public goodwill.'
        },
        riskAppetiteAndWealth: {
          en: 'Liquid assets, FMCG, hospitality, real-estate near water, and community-driven wealth.',
          hi: 'तरल संपत्ति, जन-उपयोगी वस्तुएं, आतिथ्य और स्थिर सुख-साधन।',
          hinglish: 'Liquid savings, hospitality, travel aur family-centered prosperity.'
        },
        dailyOperationalFocus: {
          en: 'Family harmony, community welfare, hospitality, creative writing, and nurturing teams.',
          hi: 'पारिवारिक सौहार्द, जन-सेवा, भावनात्मक संतुलन और रचनात्मक अभिव्यक्ति।',
          hinglish: 'Family care, community connect aur intuitive work flows.'
        },
        keyTransformationNarrative: {
          en: 'This cycle refines your emotional depth and grants you broad public affection and inner serenity.',
          hi: 'यह काल आपके मन को शांत, लोक-प्रिय और दूसरों के लिए प्रेरणास्पद बनाता है।',
          hinglish: 'Aapka aura bohot warm aur approachable ho jaata hai; log aapke paas aakar safe feel karte hain.'
        }
      };
      break;

    case 'Mars':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Mars',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Mars Mahadasha (7 Years): Fierce Initiative, Courage & Conquest',
        phaseTitleHi: 'मंगल महादशा (7 वर्ष): पराक्रम, साहसिक पहल एवं विजय अभियान',
        phaseTitleHinglish: 'Mars Dasha (7 Years): Junoon, Front-Foot Action & Decisive Conquest',
        dominantPsychology: {
          en: 'Fearless, impatient with excuses, energized by competitive challenges, and laser focused.',
          hi: 'अदम्य साहस, प्रतिस्पर्धा की भावना, बाधाओं को तोड़ने का संकल्प और स्फूर्ति।',
          hinglish: 'Junoon, high physical energy, direct confrontation aur zero excuses.'
        },
        leadershipAndAgency: {
          en: 'Frontline commander who tackles obstacles head-on and leads from the battlefield trenches.',
          hi: 'संकट में आगे रहकर नेतृत्व करने वाला और विरोधियों को परास्त करने वाला व्यक्तित्व।',
          hinglish: 'Front-foot execution, competitive market lead karna aur fearless moves.'
        },
        riskAppetiteAndWealth: {
          en: 'High-risk, high-velocity wealth through land, engineering, competitive tenders, and technical breakthroughs.',
          hi: 'भूमि, भवन, तकनीकी उपक्रम और साहसिक निवेशों से असाधारण लाभ।',
          hinglish: 'Real estate, engineering projects aur high-stakes profitable initiatives.'
        },
        dailyOperationalFocus: {
          en: 'Executing challenging milestones, high-intensity workouts, crisis resolution, and market dominance.',
          hi: 'कठिन कार्यों की पूर्णता, शारीरिक व्यायाम और प्रतिस्पर्धात्मक विजय।',
          hinglish: 'Milestone crushing, sports/gym routine aur rapid problem-solving.'
        },
        keyTransformationNarrative: {
          en: 'Mars transforms latent hesitation into raw executive steel. It empowers you to conquer territories you once feared.',
          hi: 'मंगल आपके भय को पराक्रम में बदलकर आपको विजय का मार्ग प्रशस्त करता है।',
          hinglish: 'Darr aur hesitation khatam ho jaata hai; aap directly action leke apna space claim karte ho.'
        }
      };
      break;

    case 'Rahu':
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Rahu',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Rahu Mahadasha (18 Years): Meteoric Ambition, Unconventional Mastery & Disruption',
        phaseTitleHi: 'राहु महादशा (18 वर्ष): तीव्र महत्वाकांक्षा, अप्रत्याशित उत्थान एवं नवोन्मेष',
        phaseTitleHinglish: 'Rahu Dasha (18 Years): Relentless Ambition, Boundary-Breaking & Outlier Scale',
        dominantPsychology: {
          en: 'Obsessive hunger for breakout success, defying orthodox boundaries, and global scale ambition.',
          hi: 'अपरिमित महत्वाकांक्षा, रूढ़ियों को तोड़ना, वैश्विक दृष्टिकोण और नवप्रवर्तन।',
          hinglish: 'Relentless scale ka junoon, traditional rules break karna aur out-of-the-box thinking.'
        },
        leadershipAndAgency: {
          en: 'Disruptive founder/innovator who introduces revolutionary foreign concepts and reshapes markets.',
          hi: 'क्रांतिकारी विचारक, अंतरराष्ट्रीय दृष्टि और नई तकनीकों का प्रवर्तक।',
          hinglish: 'Market disruptor, foreign tech adoption aur high-growth innovation.'
        },
        riskAppetiteAndWealth: {
          en: 'Aggressive risk-taking; massive scale ventures, foreign currencies, cutting-edge tech, and sudden windfalls.',
          hi: 'अभूतपूर्व लाभ, विदेशी स्रोत, डिजिटल तकनीक और साहसिक उद्यम।',
          hinglish: 'Tech, crypto, foreign revenue aur massive upside wale calculated bets.'
        },
        dailyOperationalFocus: {
          en: 'Building high-growth technology, expanding international networks, and exploring untapped frontiers.',
          hi: 'अंतरराष्ट्रीय संबंध, अत्याधुनिक तकनीक और तीव्र गति से विस्तार।',
          hinglish: 'Global scaling, frontier technology aur high-leverage growth.'
        },
        keyTransformationNarrative: {
          en: 'Rahu propels you beyond the confines of your ordinary background into extraordinary horizons.',
          hi: 'राहु साधारण परिधियों को तोड़कर आपको असाधारण ऊंचाइयों तक पहुंचने का सामर्थ्य देता है।',
          hinglish: 'Yeh dasha aapko local identity se nikaal kar global ya industry-level disruptor banati hai.'
        }
      };
      break;

    case 'Jupiter':
    default:
      phase = {
        level: 'mahadasha',
        mahadashaLord: 'Jupiter',
        startDate,
        endDate,
        isCurrent,
        isPast,
        isFuture,
        phaseTitleEn: 'Jupiter Mahadasha (16 Years): Timeless Wisdom, Dharma & Institutional Legacy',
        phaseTitleHi: 'गुरु महादशा (16 वर्ष): ज्ञान का विस्तार, धर्म-निष्ठा एवं सम्मानित नेतृत्व',
        phaseTitleHinglish: 'Jupiter Dasha (16 Years): Vishwas, Mentorship & Dharmic Legacy',
        dominantPsychology: {
          en: 'Expansive benevolence, principled discernment, philosophical maturity, and natural contentment.',
          hi: 'उदार दृष्टिकोण, विवेकशीलता, सत्यनिष्ठा और लोक-कल्याण की भावना।',
          hinglish: 'Apnapan, broad vision, ethical clarity aur timeless values pe focus.'
        },
        leadershipAndAgency: {
          en: 'Revered elder mentor, trusted advisory council, guiding institutions with moral authority.',
          hi: 'सर्वमान्य मार्गदर्शक, नीति-निर्माता और संस्थागत सम्मान का प्रतीक।',
          hinglish: 'Respected mentor & advisor; aapke guidance ki bohot high value hoti hai.'
        },
        riskAppetiteAndWealth: {
          en: 'Sustainable compounding, education/knowledge monetization, multi-generational family estates.',
          hi: 'ज्ञान-आधारित समृद्धि, स्थायी परिसंपत्तियां और सात्विक धन लाभ।',
          hinglish: 'Clean, dharmic wealth; long-term stable assets aur multi-generational prosperity.'
        },
        dailyOperationalFocus: {
          en: 'Mentoring leaders, writing profound works, philanthropic stewardship, and spiritual discourses.',
          hi: 'मार्गदर्शन, ज्ञान-प्रसार, परोपकार और आध्यात्मिक चिंतन।',
          hinglish: 'Mentorship, teaching, ethical consulting aur family spiritual growth.'
        },
        keyTransformationNarrative: {
          en: 'Jupiter elevates your life into a revered lighthouse of wisdom for your family and community.',
          hi: 'गुरु आपके जीवन को समाज और परिवार के लिए प्रकाश-स्तंभ बना देते हैं।',
          hinglish: 'Aapki credibility aur public trust peak pe hoti hai; aapka legacy-building phase shuru hota hai.'
        }
      };
      break;
  }
  return phase;
}

/**
 * Generate Tactical Antardasha Phase Details (1-2 Year Sub-Epoch)
 */
export function getAntardashaPhaseDetails(
  mahaLord: PlanetName,
  antarLord: PlanetName,
  startDate: string,
  endDate: string,
  isCurrent: boolean,
  isPast: boolean,
  isFuture: boolean,
  archetype: ChartArchetypeProfile
): DashaEvolutionPhase {
  // Synthesize customized Parashari blend of Mahadasha (environment) + Antardasha (tactical agent)
  return {
    level: 'antardasha',
    mahadashaLord: mahaLord,
    antardashaLord: antarLord,
    startDate,
    endDate,
    isCurrent,
    isPast,
    isFuture,
    phaseTitleEn: `${mahaLord} - ${antarLord} Antardasha: Tactical Shift & Role Execution`,
    phaseTitleHi: `${mahaLord} - ${antarLord} अंतर्दशा: विशिष्ट कार्य-विस्तार एवं रणनीतिक भूमिका`,
    phaseTitleHinglish: `${mahaLord} - ${antarLord} Sub-Period: Tactical Role Pivot & Focus`,
    dominantPsychology: {
      en: `In this ${mahaLord} macro-cycle, the activation of ${antarLord} channels your mindset into distinct tactical execution, sharpening cognitive bandwidth toward ${antarLord.toLowerCase()} affairs.`,
      hi: `${mahaLord} महादशा के अंतर्गत ${antarLord} का प्रभाव आपकी मानसिक ऊर्जा को केंद्रित करके व्यावहारिक निर्णय लेने में सहायता करता है।`,
      hinglish: `Is sub-phase mein ${mahaLord} ke broad environment mein ${antarLord} ka tactical focus aapki daily thinking aur decision-making ko lead karta hai.`
    },
    leadershipAndAgency: {
      en: `Direct organizational posture shifts toward ${antarLord}'s natural domain: managing key dependencies, reorganizing team mandates, and claiming operational ownership.`,
      hi: `${antarLord} के अनुसार कार्यक्षेत्र में नई जिम्मेदारियां, दल का पुनर्गठन और प्रत्यक्ष प्रशासनिक प्रभाव का विस्तार।`,
      hinglish: `Team delegation, project ownership aur authority expansion specific to ${antarLord}'s core functional mandate.`
    },
    riskAppetiteAndWealth: {
      en: `Financial liquidity and capital allocation align with ${mahaLord}-${antarLord} chemistry: disciplined resource buffering balanced with strategic investments.`,
      hi: `वित्तीय तरलता का संतुलन, दीर्घकालिक बचत और सोच-समझकर किए गए पूंजीगत निवेश।`,
      hinglish: `Cash flow management, strategic business expenses aur calculated resource allocation.`
    },
    dailyOperationalFocus: {
      en: `High-priority deliverables: executing milestone roadmaps, removing workflow bottlenecks, and cementing key partner alignments.`,
      hi: `दैनिक प्राथमिकताएं: प्रमुख परियोजनाओं का समय पर समापन, परिचालन बाधाओं का निवारण और महत्वपूर्ण अनुबंध।`,
      hinglish: `Core deliverables deliver karna, system bottlenecks door karna aur key partnerships finalize karna.`
    },
    keyTransformationNarrative: {
      en: `This Antardasha serves as a critical tactical bridge, transforming abstract potential into verifiable real-world progress and institutional credibility.`,
      hi: `यह अंतर्दशा आपके प्रयासों को ठोस परिणामों और कार्यक्षेत्र में स्थायी प्रतिष्ठा में बदलती है।`,
      hinglish: `Yeh sub-period aapko ek specific milestone complete karke next level pe promote karta hai.`
    }
  };
}

/**
 * Generate Operational Pratyantardasha Phase Details (1-3 Month Micro-Quarter)
 */
export function getPratyantardashaPhaseDetails(
  mahaLord: PlanetName,
  antarLord: PlanetName,
  pdLord: PlanetName,
  startDate: string,
  endDate: string,
  isCurrent: boolean,
  isPast: boolean,
  isFuture: boolean,
  archetype: ChartArchetypeProfile
): DashaEvolutionPhase {
  return {
    level: 'pratyantardasha',
    mahadashaLord: mahaLord,
    antardashaLord: antarLord,
    pratyantardashaLord: pdLord,
    startDate,
    endDate,
    isCurrent,
    isPast,
    isFuture,
    phaseTitleEn: `${mahaLord} - ${antarLord} - ${pdLord} Pratyantardasha: Quarterly Micro-Catalyst`,
    phaseTitleHi: `${mahaLord} - ${antarLord} - ${pdLord} प्रत्यंतर्दशा: त्रैमासिक परिचालन गति`,
    phaseTitleHinglish: `${mahaLord} - ${antarLord} - ${pdLord} PD: Quarterly Action Sprint`,
    dominantPsychology: {
      en: `Immediate 1-3 month cognitive tone dominated by ${pdLord}'s acute trigger: rapid decision loops, acute mental bandwidth, and focused prioritization.`,
      hi: `इस 1-3 महीने के कालखंड में ${pdLord} का प्रभाव तात्कालिक निर्णयों, त्वरित सोच और मानसिक एकाग्रता को संचालित करता है।`,
      hinglish: `Immediate 1-3 months mein ${pdLord} ka trigger aapke mood, urgency aur daily focus ko fast pace pe le aata hai.`
    },
    leadershipAndAgency: {
      en: `Quarterly leadership maneuvers: frontline conflict resolution, clearing team backlogs, and spearheading tactical sprints.`,
      hi: `त्रैमासिक नेतृत्व: तात्कालिक समस्याओं का समाधान, त्वरित कार्यवाही और कार्यदल को गति प्रदान करना।`,
      hinglish: `Sprint leadership, immediate project push aur active friction resolve karna.`
    },
    riskAppetiteAndWealth: {
      en: `Short-term cash-flow optimization: monitoring quarterly outflows, securing immediate receivables, and defensive risk hedges.`,
      hi: `अल्पकालिक नकदी प्रवाह का नियंत्रण, आवश्यक व्ययों की समीक्षा और वित्तीय सुरक्षा।`,
      hinglish: `Quarterly budget control, immediate payments collection aur short-term risk control.`
    },
    dailyOperationalFocus: {
      en: `Weekly sprint targets: deadline enforcement, critical meeting cadences, resolving urgent project dependencies, and preserving sleep hygiene.`,
      hi: `साप्ताहिक लक्ष्य: समयबद्ध कार्य, महत्वपूर्ण संवाद, तात्कालिक जिम्मेदारियों का निर्वहन और शारीरिक ऊर्जा का संतुलन।`,
      hinglish: `Weekly sprint completion, client meetings, urgent follow-ups aur physical health balance.`
    },
    keyTransformationNarrative: {
      en: `This micro-catalyst quarter resolves immediate operational friction and sets the concrete foundation for the next Antardasha transition.`,
      hi: `यह सूक्ष्म कालखंड परिचालन की तात्कालिक बाधाओं को दूर कर आगामी बड़ी सफलता का मार्ग प्रशस्त करता है।`,
      hinglish: `Yeh quarterly window short-term friction door karke key operational deliverables unlock karti hai.`
    }
  };
}

/**
 * Calculates complete nested Antardasha and Pratyantardasha hierarchy
 */
function buildNestedDashaHierarchy(
  mahaPlanet: PlanetName,
  startDateStr: string,
  endDateStr: string,
  nowMs: number,
  archetype: ChartArchetypeProfile
): { antardashas: DashaEvolutionPhase[] } {
  const MS_PER_DAY = 86400000;
  const startMs = new Date(startDateStr).getTime();
  const endMs = new Date(endDateStr).getTime();
  const totalDurationDays = Math.max(1, (endMs - startMs) / MS_PER_DAY);
  const mahaIdx = mahaPlanet ? Math.max(0, (DASHA_SEQUENCE || []).indexOf(mahaPlanet)) : 0;

  const antardashas: DashaEvolutionPhase[] = [];
  let subCurrentMs = startMs;

  for (let j = 0; j < 9; j++) {
    const antarPlanet = DASHA_SEQUENCE[(mahaIdx + j) % 9];
    const antarYears = PLANET_YEARS[antarPlanet] || 10;
    // Antardasha proportion of 120 total years
    const antarDays = (totalDurationDays * antarYears) / 120;
    const antarEndMs = Math.min(endMs, subCurrentMs + antarDays * MS_PER_DAY);

    const antarStartStr = new Date(subCurrentMs).toISOString().split('T')[0];
    const antarEndStr = new Date(antarEndMs).toISOString().split('T')[0];
    const isCurAntar = nowMs >= subCurrentMs && nowMs < antarEndMs;
    const isPastAntar = nowMs >= antarEndMs;
    const isFutAntar = nowMs < subCurrentMs;

    const antarPhase = getAntardashaPhaseDetails(
      mahaPlanet,
      antarPlanet,
      antarStartStr,
      antarEndStr,
      isCurAntar,
      isPastAntar,
      isFutAntar,
      archetype
    );

    // Build Pratyantardashas for this Antardasha using authoritative calculator
    const pratyantarItems = calculatePratyantardashas(antarPlanet, antarStartStr, antarEndStr);
    const pratyantardashas: DashaEvolutionPhase[] = pratyantarItems.map(item => {
      const isCurPd = item.isCurrent;
      const pdEndMs = new Date(item.endDate).getTime();
      const pdStartMs = new Date(item.startDate).getTime();
      const isPastPd = nowMs >= pdEndMs;
      const isFutPd = nowMs < pdStartMs;

      return getPratyantardashaPhaseDetails(
        mahaPlanet,
        antarPlanet,
        item.planet,
        item.startDate.split('T')[0],
        item.endDate.split('T')[0],
        isCurPd,
        isPastPd,
        isFutPd,
        archetype
      );
    });

    antarPhase.pratyantardashas = pratyantardashas;
    antardashas.push(antarPhase);
    subCurrentMs = antarEndMs;
  }

  return { antardashas };
}

/**
 * Main Engine: Synthesizes Dasha-Driven Life Phase & Evolution Report
 */
export function generateDashaEvolutionReport(
  kundali: CompleteKundali,
  currentDate: Date = new Date()
): DashaEvolutionReport {
  const archetype = identifyChartArchetype(kundali);
  const nowMs = currentDate.getTime();
  const allDashas = kundali.dashas || [];

  let currentMaha: DashaPeriod | null = null;
  let currentAntar: DashaPeriod | null = null;
  let nextMaha: DashaPeriod | null = null;

  // Locate current and next Mahadashas
  for (let i = 0; i < allDashas.length; i++) {
    const d = allDashas[i];
    const sMs = new Date(d.startDate).getTime();
    const eMs = new Date(d.endDate).getTime();

    if (nowMs >= sMs && nowMs < eMs) {
      currentMaha = d;
      if (i + 1 < allDashas.length) {
        nextMaha = allDashas[i + 1];
      }
      // Locate active Antardasha
      if (d.antardashas && d.antardashas.length > 0) {
        for (const sub of d.antardashas) {
          const subSMs = new Date(sub.startDate).getTime();
          const subEMs = new Date(sub.endDate).getTime();
          if (nowMs >= subSMs && nowMs < subEMs) {
            currentAntar = sub;
            break;
          }
        }
      }
      break;
    }
  }

  // Fallback to first if not located
  if (!currentMaha && allDashas.length > 0) {
    currentMaha = allDashas[0];
    nextMaha = allDashas[1] || null;
  }

  const activeMahaLord = currentMaha?.planet || 'Jupiter';
  const activeStart = currentMaha?.startDate || new Date().toISOString().split('T')[0];
  const activeEnd = currentMaha?.endDate || new Date(Date.now() + 5 * 365 * 86400000).toISOString().split('T')[0];

  const activePhaseSummary = getDashaPhaseDetails(
    activeMahaLord,
    activeStart,
    activeEnd,
    true,
    false,
    false,
    archetype
  );

  const activeNested = buildNestedDashaHierarchy(
    activeMahaLord,
    activeStart,
    activeEnd,
    nowMs,
    archetype
  );
  activePhaseSummary.antardashas = activeNested.antardashas;

  // Build Chronological Phase array with full nested hierarchies
  const allChronologicalPhases: DashaEvolutionPhase[] = allDashas.map((d) => {
    const sMs = new Date(d.startDate).getTime();
    const eMs = new Date(d.endDate).getTime();
    const isCur = nowMs >= sMs && nowMs < eMs;
    const isPast = nowMs >= eMs;
    const isFut = nowMs < sMs;

    const phase = getDashaPhaseDetails(
      d.planet,
      d.startDate,
      d.endDate,
      isCur,
      isPast,
      isFut,
      archetype
    );

    const nested = buildNestedDashaHierarchy(
      d.planet,
      d.startDate,
      d.endDate,
      nowMs,
      archetype
    );
    phase.antardashas = nested.antardashas;

    return phase;
  });

  // Build Transition Bridges
  const upcomingTransitions: DashaTransitionBridge[] = [];
  if (currentMaha && nextMaha) {
    upcomingTransitions.push(
      getMahadashaTransitionShift(
        currentMaha.planet,
        nextMaha.planet,
        archetype,
        nextMaha.startDate
      )
    );
  }

  // Also check immediate past transition if exists
  const currentIdx = allDashas.findIndex((d) => d.planet === currentMaha?.planet && d.startDate === currentMaha?.startDate);
  if (currentIdx > 0) {
    const prevMaha = allDashas[currentIdx - 1];
    upcomingTransitions.unshift(
      getMahadashaTransitionShift(
        prevMaha.planet,
        currentMaha.planet,
        archetype,
        currentMaha.startDate
      )
    );
  }

  return {
    archetype,
    currentMahadasha: currentMaha,
    currentAntardasha: currentAntar,
    nextMahadasha: nextMaha,
    activePhaseSummary,
    upcomingTransitions,
    allChronologicalPhases
  };
}
